import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSession } from "@/hooks/auth-hooks";
import { trackGa4EventOnce } from "@/lib/ga4";
import { db } from "@/lib/instantdb";

type Workspace = {
	id: string;
	name: string;
	imageUrl?: string | null;
	onboardingContentFormats?: string | null;
	onboardingPriorityPlatforms?: string | null;
	onboardingStatus?: string | null;
	slug: string;
	role: string;
	isActive: boolean;
	memberId: string;
};

type McpKeySummary = {
	configured: boolean;
	keyPrefix: string | null;
	lastUsedAt: string | null;
	name: string | null;
	updatedAt: string | null;
};

interface DashboardContextValue {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
	} | null;
	isPending: boolean;
	isLoading: boolean;
	workspace: Workspace | null;
	workspaces: Workspace[];
	isWorkspacesLoading: boolean;
	switchWorkspace: (workspaceId: string) => Promise<void>;
	isMcpConnected: boolean;
	isMcpLoading: boolean;
}

export const DashboardContext =
	React.createContext<DashboardContextValue | null>(null);

export function useDashboardContext() {
	const context = React.useContext(DashboardContext);
	if (!context) {
		throw new Error(
			"useDashboardContext must be used within DashboardProvider",
		);
	}
	return context;
}

interface DashboardProviderProps {
	children: React.ReactNode;
}

function useInstantDbAuth(userId: string | undefined) {
	const instantAuth = db.useAuth();
	const isCurrentInstantUser = Boolean(
		userId && instantAuth.user?.id === userId,
	);

	const { data } = useQuery({
		enabled: Boolean(userId && !isCurrentInstantUser),
		queryFn: async () => {
			const res = await fetch("/api/instant-token");
			if (!res.ok) return { token: undefined } as { token?: string };
			return res.json() as Promise<{ token?: string }>;
		},
		queryKey: ["instant-token", userId],
		staleTime: Infinity,
	});

	const token = data?.token;
	React.useEffect(() => {
		if (!token || isCurrentInstantUser) return;
		void db.auth.signInWithToken(token).catch((error) => {
			console.error("[InstantDB] Failed to sign in with token", error);
		});
	}, [isCurrentInstantUser, token]);

	return {
		isReady: isCurrentInstantUser,
		isLoading: Boolean(userId && !isCurrentInstantUser),
	};
}

export function DashboardProvider({ children }: DashboardProviderProps) {
	const { user, isPending } = useSession();

	const instantAuth = useInstantDbAuth(user?.id);

	// Workspace membership is permissioned by Instant auth, so wait until it
	// matches the Better Auth user before deciding whether the user has none.
	const { data: membersData, isLoading: isMembersLoading } = db.useQuery(
		user?.id && instantAuth.isReady
			? {
					workspaceMembers: {
						$: { where: { "user.id": user.id } },
						workspace: {},
					},
				}
			: null,
	);

	const workspaceMembers = membersData?.workspaceMembers;
	const isWorkspaceMembersLoaded = Array.isArray(workspaceMembers);

	const workspaces: Workspace[] = React.useMemo(() => {
		if (!workspaceMembers) return [];
		return workspaceMembers.flatMap((m) => {
			const ws = m.workspace;
			if (!ws) return [];
			return {
				id: ws.id,
				name: ws.name,
				imageUrl: ws.imageUrl ?? null,
				onboardingContentFormats: ws.onboardingContentFormats ?? null,
				onboardingPriorityPlatforms: ws.onboardingPriorityPlatforms ?? null,
				onboardingStatus: ws.onboardingStatus ?? null,
				slug: ws.slug,
				role: m.role,
				isActive: m.isActive,
				memberId: m.id,
			};
		});
	}, [workspaceMembers]);

	const activeWorkspace = workspaces.find((ws) => ws.isActive) ?? null;

	const { data: userData } = db.useQuery(
		user?.id ? { users: { $: { where: { id: user.id } } } } : null,
	);
	const dbUser = userData?.users?.[0];

	React.useEffect(() => {
		if (!user?.id || !dbUser?.createdAt) return;
		const createdAt = new Date(dbUser.createdAt).getTime();
		if (!Number.isFinite(createdAt)) return;
		const signupWindowMs = 15 * 60 * 1000;
		if (Date.now() - createdAt > signupWindowMs) return;
		const storageKey = `ar:ga4:sign_up:${user.id}`;
		if (sessionStorage.getItem("ar:ga4:sign_up_tracked_this_session") === "1") {
			localStorage.setItem(storageKey, "1");
			sessionStorage.removeItem("ar:ga4:sign_up_tracked_this_session");
			return;
		}
		trackGa4EventOnce(`sign_up:${user.id}`, "sign_up", {
			method: dbUser.signupMethod ?? "unknown",
		});
	}, [dbUser?.createdAt, dbUser?.signupMethod, user?.id]);

	const mcpKeyQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const res = await fetch("/api/mcp/key", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch MCP status");
			return res.json() as Promise<{ key: McpKeySummary }>;
		},
		queryKey: ["dashboard", "mcp-key"],
		refetchInterval: 10_000,
	});

	const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
	const lastSeen = dbUser?.agentLastSeenAt ?? mcpKeyQuery.data?.key?.lastUsedAt;
	const isMcpConnected = Boolean(
		lastSeen && new Date(lastSeen).getTime() > fiveMinutesAgo,
	);

	const switchWorkspace = React.useCallback(async (workspaceId: string) => {
		const res = await fetch("/api/workspaces", {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
			},
			body: JSON.stringify({ workspaceId }),
		});
		if (!res.ok) throw new Error("Failed to switch workspace");
	}, []);

	const value: DashboardContextValue = {
		user: user
			? { id: user.id, name: user.name, email: user.email, image: user.image }
			: null,
		isPending,
		isLoading: isPending || instantAuth.isLoading,
		workspace: activeWorkspace,
		workspaces,
		isWorkspacesLoading:
			instantAuth.isLoading || isMembersLoading || !isWorkspaceMembersLoaded,
		switchWorkspace,
		isMcpConnected,
		isMcpLoading: mcpKeyQuery.isLoading,
	};

	return (
		<DashboardContext.Provider value={value}>
			{children}
		</DashboardContext.Provider>
	);
}
